<template>
  <div
    class="node-message cursor-pointer"
    :class="{
      'node-message--selected': selected,
    }"
    @click="handleSelect"
  >
    <div class="node-message__header">
      <div class="node-message__title">
        <MessageOutlined class="node-message__icon--profile" />
        <h5 class="heading-h7">
          {{ title }}
          <span class="acronym">{{ nodeIdentifier }}</span>

          <a-tooltip
            v-if="showQueueWarning"
            title="Este campo se encontraba utilizando una cola que ha sido eliminada. Podrás volver a activar tu chatbot cuando selecciones una nueva cola de transferencia."
            placement="topLeft"
            arrowPointAtCenter
          >
            <WarningFilled style="color: #faad14" />
          </a-tooltip>
          <a-tooltip
            v-else-if="node?.deleted_variable"
            title="Este campo se encuentra sin una variable, selecciona una para poder activar tu chatbot."
            placement="topLeft"
            arrowPointAtCenter
          >
            <WarningFilled style="color: #faad14" />
          </a-tooltip>
          <links-attachments :dataSource="dataSource" @onPreview="handleShowMedia" />
        </h5>
      </div>
      <div>
        <a-tooltip v-if="showInfo" placement="bottom">
          <template #title>
            <span>
              Recuerda que no se podrán agregar más mensajes en este y se resolverá el ticket.
            </span>
          </template>
          <InfoCircleOutlined class="node-message__icon--info" />
        </a-tooltip>
        <em class="info-2 node-message__action">
          <a-tooltip placement="right">
            <template #title v-if="hasActionTooltip">
              {{ `${action} “${dataSource?.queue?.name}”` }}
            </template>

            {{ `${action}${formattedName}` }}
          </a-tooltip>
        </em>
      </div>
    </div>
    <div class="node-message__body">
      <p
        class="body-2 node-message__description mrg-bottom-0 ellipsis text--left"
        :title="description"
      >
        {{ description }}
      </p>
      <div class="node-message__actions actions">
        <a-button
          type="link"
          :danger="!disabled"
          class="button"
          @click.stop="() => !disabled && $emit('onDelete')"
          :disabled="disabled"
        >
          Eliminar
        </a-button>
      </div>
    </div>
  </div>
</template>
<script>
import { MessageOutlined, InfoCircleOutlined, WarningFilled } from "@ant-design/icons-vue";

import { BotActions } from "@/app/chatbots/utils/enums";
import { inject } from "vue";
import LinksAttachments from "@/app/chatbots/components/molecules/LinksAttachments.vue";
import { AcronymByNode } from "@/app/chatbots/utils/texts";

export default {
  name: "MessageNode",
  props: {
    title: { type: String, default: "Mensaje" },
    description: { type: String, default: "Descripcion" },
    dataSource: { type: Object, required: false },
    action: { type: String, default: "Finalizar chatbot" },
    selected: { type: Boolean, required: false, default: false },
    showInfo: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, default: false },
    index: { type: Number, default: 1 },
  },
  components: {
    MessageOutlined,
    InfoCircleOutlined,
    WarningFilled,
    LinksAttachments,
  },
  setup() {
    const fetchChatbot = inject("fetchChatbot");

    return { fetchChatbot };
  },
  computed: {
    isTransferAction() {
      return this.dataSource?.action === BotActions.TransferToQueue;
    },
    hasActionTooltip() {
      return this.dataSource?.queue?.name?.length > 25 && this.isTransferAction;
    },
    showQueueWarning() {
      return this.isTransferAction && this.dataSource?.deleted_queue;
    },
    formattedName() {
      if (!this.isTransferAction || this.showQueueWarning) return "";

      const { queue } = this.dataSource;
      const formattedName =
        queue?.name?.length > 25 ? `${queue?.name?.substring(0, 25)}...` : queue?.name;

      return formattedName ? ` “${formattedName}”` : "";
    },
    nodeIdentifier() {
      const acronym = AcronymByNode[this.dataSource.type];
      const nodeId = this.dataSource.id;

      if (!acronym) return "";

      return `(${acronym}${nodeId})`;
    },
  },
  watch: {
    showQueueWarning(newVal, prevVal) {
      if (prevVal) this.fetchChatbot();
    },
  },
  methods: {
    handleSelect() {
      this.$emit("onSelect");
    },
    handleShowMedia(mediaData) {
      this.$emit("onPreviewMedia", mediaData);
    },
  },
};
</script>
<style lang="sass" scoped>
.node-message
  border: 1px solid $gray-5
  border-radius: 8px
  margin-bottom: 8px
  padding: 12px
  &__header
    display: flex
    justify-content: space-between
    align-items: center
    margin-bottom: 6px
  &__title
    display: flex
  &__description
    text-align: left
    color: $gray-8
    margin-right: 20px
    text-overflow: ellipsis
    overflow: hidden
    text-wrap: nowrap
  &__icon--profile
    font-size: 20px
    color: $primary
    margin-right: 8px
  &__icon--info
    font-size: 14px
    color: $gray-7
    // margin-right: 8px
  &__action
    color: $gray-7
  &__body
    display: flex
    justify-content: space-between
  &--selected
    border: 1px solid $primary
    background-color: $blue-1
  &:hover
    border: 1px solid $primary

.actions
  flex: 0 0 auto

.cursor-pointer
  cursor: pointer

.heading-h7
  color: $gray-9
  font-weight: 600
  font-size: 14px

h5, p
  margin-bottom: 0

.button
  padding: 0
  height: 100%

.acronym
  font-size: 12px
  font-weight: 400
  color: $text-tertiary
</style>
